// dependencies
import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import Select from "react-select";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { Timestamp } from "firebase/firestore";

// components
import BB8LoadingPage from "../../components/layout/BB8LoadingPage";
import PrimaryBtn from "../../components/btns/PrimaryBtn"
import SecondaryBtn from "../../components/btns/SecondaryBtn"

// api
import { updateDocument } from "../../api/firebase/firebase"

// hook
import { useFetchProjectById } from "../../hooks/useFirebase";

// styles
import styles from "./css/AddProject.module.css";


const EditProject = () => {
    const { id } = useParams();
    // fetch project from ID in url
    const { project, loading, error } = useFetchProjectById(id)
    const [formData, setFormData] = useState(null);

    // once project is fetched -> populate formData
    useEffect(() => {
        if (!loading && project && project.title) {
            setFormData({
                title: project.title,
                description: project.description || "",
                content: project.content || "",
                ytLink: project.ytLink || "",
                githubLink: project.githubLink || "",
                liveLink: project.liveLink || "",
                imgs: project.imgs || [],
                tags: project.tags || [],
                features: project.features || [],
                technologies: project.technologies || [],
                thumbnail: project.thumbnail || "",
                dateStarted: project.dateStarted ? new Date(project.dateStarted.seconds * 1000) : null,
                dateCompleted: project.dateCompleted ? new Date(project.dateCompleted.seconds * 1000) : null,
            })
        }
    }, [project, loading]);



    // tags helper
    const handleTagsChange = (selectedOptions) => {
        setFormData({ ...formData, tags: selectedOptions.map(option => option.value) })
    }

    // feature helpers
    const addFeature = (feature) => {
        if (feature.trim()) {
            setFormData({ ...formData, features: [...formData.features, feature.trim()] });
        }
    };

    const removeFeature = (index) => {
        const updated = [...formData.features];
        updated.splice(index, 1);
        setFormData({ ...formData, features: updated });
    };

    // technology helpers
    const addTechnologyType = () => {
        setFormData({
            ...formData,
            technologies: [...formData.technologies, { type: "", items: [] }],
        });
    };

    const updateTechnologyType = (index, value) => {
        const updated = [...formData.technologies];
        updated[index].type = value;
        setFormData({ ...formData, technologies: updated });
    };

    const addTechnologyItem = (index, item) => {
        if (!item.trim()) return;
        const updated = [...formData.technologies];
        updated[index].items.push(item.trim());
        setFormData({ ...formData, technologies: updated });
    };

    const removeTechnologyItem = (typeIndex, itemIndex) => {
        const updated = [...formData.technologies];
        updated[typeIndex].items.splice(itemIndex, 1);
        setFormData({ ...formData, technologies: updated });
    };

    const removeTechnologyType = (index) => {
        const updated = [...formData.technologies];
        updated.splice(index, 1);
        setFormData({ ...formData, technologies: updated });
    };



    // handle form submit
    const handleSubmit = async () => {
        try {
            const updatedFormData = {
                ...formData,
                dateStarted: formData.dateStarted 
                    ? Timestamp.fromDate(new Date(formData.dateStarted))
                    : null,
                dateCompleted: formData.dateCompleted 
                    ? Timestamp.fromDate(new Date(formData.dateCompleted))
                    : null,
                createdAt: new Date().toISOString(),
            };
            //console.log("new formData", updatedFormData);

            await updateDocument("projects", id, updatedFormData);
            toast("Project updated successfully", { type: "success" });
        } catch (error) {
            console.error("Error adding project:", error);
            toast("Error updating project", { type: "error" });
        }
    }


    if (loading) return <><BB8LoadingPage /></>;
    if (error) return <p>Error: {error}</p>;
    if (!project) return <p>Project not found.</p>;
    if (!formData) return <p>Initializing form...</p>;

    
    return (
        <div className={styles.formContainer}>
            <div className={styles.form}>
                <div className={styles.addTitle}>Edit Project</div>

                {/* Title, Description, Content */}
                <div className={styles.inputContainer}>
                    <label htmlFor="title" className={styles.labelText}>
                        Title <span>*</span>
                    </label>
                    <input
                        className={`${styles.textInput} ${styles.titleText}`}
                        type="text"
                        name="title"
                        value={formData?.title}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                title: e.target.value,
                            })
                        }
                    />
                </div>

                <div className={styles.inputContainer}>
                    <label htmlFor="description" className={styles.labelText}>
                        Description <span>*</span>
                    </label>
                    <textarea
                        className={styles.largeTextInput}
                        name="description"
                        value={formData?.description}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                description: e.target.value,
                            })
                        }
                    />
                </div>

                <div className={styles.inputContainer}>
                    <label htmlFor="content" className={styles.labelText}>
                        Content <span>*</span>
                    </label>
                    <textarea
                        className={styles.largeTextInput}
                        name="content"
                        value={formData?.content}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                content: e.target.value,
                            })
                        }
                    />
                </div>

                {/* Features */}
                <div className={styles.inputContainer}>
                    <label className={styles.labelText}>
                        Features
                    </label>
                    <div className={styles.flexRow}>
                        <input
                            className={styles.textInput}
                            placeholder="Add a feature"
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    addFeature(e.target.value);
                                    e.target.value = "";
                                }
                            }}
                        />
                    </div>
                    <ul className={styles.tagList}>
                        {formData?.features.map((feature, i) => (
                            <li key={i} className={styles.tagItem}>
                                {feature}
                                <button type="button" onClick={() => removeFeature(i)}>
                                    x
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Technologies */}
                <div className={styles.inputContainer}>
                    <label className={styles.labelText}>
                        Technologies
                    </label>
                    {formData?.technologies.map((tech, index) => (
                        <div key={index} className={styles.techGroup}>
                            <div className={styles.flexRow}>
                                <input
                                    className={styles.textInput}
                                    value={tech.type}
                                    placeholder="Type (e.g. Frontend)"
                                    onChange={(e) =>
                                        updateTechnologyType(index, e.target.value)
                                    }
                                />
                                <SecondaryBtn 
                                    onClick={() => removeTechnologyType(index)} 
                                    text={"Remove Type"} 
                                />
                            </div>

                            <div className={styles.flexRow}>
                                <input
                                    className={styles.textInput}
                                    placeholder="Add item"
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            addTechnologyItem(
                                                index,
                                                e.target.value
                                            );
                                            e.target.value = "";
                                        }
                                    }}
                                />
                            </div>

                            <ul className={styles.tagList}>
                                {tech.items.map(
                                    (item, itemIndex) => (
                                        <li key={itemIndex} className={ styles.tagItem }>
                                            {item}
                                            <button
                                                type="button"
                                                onClick={() => removeTechnologyItem(index, itemIndex)}
                                            >
                                                x
                                            </button>
                                        </li>
                                    )
                                )}
                            </ul>
                        </div>
                    ))}

                    <PrimaryBtn 
                        onClick={addTechnologyType}
                        text={"+ Add Technology Type"}
                    />
                </div>

                {/* YouTube, GitHub, Live Link */}
                <div className={styles.inputContainer}>
                    <label htmlFor="ytLink" className={styles.labelText}>
                        YouTube Link
                    </label>
                    <input
                        className={styles.textInput}
                        type="url"
                        name="ytLink"
                        value={formData?.ytLink}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                ytLink: e.target.value,
                            })
                        }
                    />
                </div>

                <div className={styles.inputContainer}>
                    <label htmlFor="githubLink" className={styles.labelText}>
                        GitHub Link
                    </label>
                    <input
                        className={styles.textInput}
                        type="url"
                        name="githubLink"
                        value={formData?.githubLink}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                githubLink: e.target.value,
                            })
                        }
                    />
                </div>

                <div className={styles.inputContainer}>
                    <label htmlFor="githubLink" className={styles.labelText}>
                        Live Link
                    </label>
                    <input
                        className={styles.textInput}
                        type="url"
                        name="liveLink"
                        value={formData?.liveLink}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                liveLink: e.target.value,
                            })
                        }
                    />
                </div>

                {/* Tags */}
                <div className={styles.inputContainer}>
                    <label htmlFor="tags" className={styles.labelText}>
                        Tags <span>*</span>
                    </label>
                    <Select
                        isMulti
                        options={[
                            { value: "Active", label: "Active" },
                            { value: "Completed", label: "Completed" },
                            { value: "Uni Project", label: "Uni Project" },
                            { value: "Personal Project", label: "Personal Project" },
                            { value: "Freelance Project", label: "Freelance Project" },
                        ]}
                        value={formData?.tags.map(tag => ({ value: tag, label: tag }))}
                        onChange={handleTagsChange}
                    />
                </div>

                {/* Dates */}
                <div className={styles.inputContainer}>
                    <label htmlFor="dateStarted" className={styles.labelText}>
                        Date Started
                    </label>
                    <DatePicker
                        selected={formData.dateStarted}
                        onChange={(date) => {
                            setFormData({
                                ...formData,
                                dateStarted: date,
                            });
                        }}
                        dateFormat="yyyy/MM/dd"
                    />
                </div>

                <div className={styles.inputContainer}>
                    <label htmlFor="dateCompleted" className={styles.labelText}>
                        Date Completed
                    </label>
                    <DatePicker
                        selected={formData.dateCompleted}
                        onChange={(date) =>
                            setFormData({
                                ...formData,
                                dateCompleted: date,
                            })
                        }
                        dateFormat="yyyy/MM/dd"
                    />
                </div>

                {/* Submit */}
                <div className={styles.submitContainer}>
                    <PrimaryBtn 
                        onClick={handleSubmit}
                        text={"Save Changes"}
                    />
                </div>
            </div>
        </div>
    );
}

export default EditProject
